import { Block, type Change, type SelectionRange, TextRange, type Value } from '@gitbook/slate';
import { List } from '@gitbook/util-immutable';

import {
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_UL,
    type BlockOrderedListData,
    LIST_BLOCKS,
} from '../types';
import { getCurrentItem, getItemDepth, getItemsAtRange, getListForItem, isList } from './utils';

/**
 * Decreases the depth of the selected list items. Root items stay in place and the sub-items
 * the selection extends into are outdented instead.
 */
export function decreaseItemsDepth(change: Change, items?: List<Block>): Change {
    return updateItemsDepth(change, items, decreaseItemsDepthWithoutNormalization);
}

/**
 * Increases the depth of the selected list items.
 */
export function increaseItemsDepth(change: Change, items?: List<Block>): Change {
    return updateItemsDepth(change, items, increaseItemsDepthWithoutNormalization);
}

/**
 * Split a list item at the start of the current range.
 */
export function splitListItem(change: Change): Change {
    const { value } = change;
    const currentItem = getCurrentItem(value);

    const { startKey, startOffset } = value;
    if (!currentItem || !startKey) {
        return change;
    }

    return change.splitDescendantsByKey(currentItem.key, startKey, startOffset);
}

/**
 * Unwrap items at range from their list.
 */
export function unwrapList(change: Change): Change {
    const items = getItemsAtRange(change.value);
    const firstItem = items.first();
    if (!firstItem) {
        return change;
    }

    // Unwrap the items from their list
    items.forEach((item) => change.unwrapNodeByKey(item.key, { normalize: false }));

    // Parent of the list of the items
    const parent = change.value.document.getParent(firstItem.key);
    if (!parent) {
        throw new Error('Unwrapped list item has no parent');
    }

    let index = parent.getChildIndex(firstItem.key);

    // Unwrap the items' children
    items.forEach((item) => {
        item.nodes.forEach((node) => {
            change.moveNodeByKey(node.key, parent.key, index, {
                normalize: false,
            });

            index += 1;
        });
    });

    // Finally, remove the now empty items
    items.forEach((item) => change.removeNodeByKey(item.key, { normalize: false }));

    return change;
}

/**
 * Wrap the blocks in the current selection in a new list. Selected
 * lists are merged together.
 */
export function wrapInList(
    change: Change,
    type: string = BLOCK_LIST_UL,
    data: BlockOrderedListData = {}
): Change {
    const selectedBlocks = getHighestSelectedBlocks(change.value);
    if (selectedBlocks.isEmpty()) {
        return change;
    }

    // Wrap in container
    change.wrapBlock({ type, data }, { normalize: false });

    // Wrap in list items
    selectedBlocks.forEach((node) => {
        if (isList(node)) {
            // Merge its items with the created list
            node.nodes.forEach(({ key }) => change.unwrapNodeByKey(key, { normalize: false }));
        } else {
            change.wrapBlockByKey(node.key, BLOCK_LIST_ITEM, { normalize: false });
        }
    });

    return change.normalize();
}

/**
 * Apply `update` to the given items, or the items at range, without normalizing in between,
 * then normalize once.
 */
function updateItemsDepth(
    change: Change,
    items: List<Block> | undefined,
    update: (change: Change, items: List<Block>) => Change
): Change {
    const { document } = change.value;
    const selectedItems = items ?? getItemsAtRange(change.value);
    const firstItem = selectedItems.first();
    const parent = firstItem ? document.getParent(firstItem.key) : null;
    if (!parent) {
        return change;
    }

    change.withoutNormalization(() => update(change, selectedItems));
    if (change.value.document === document) {
        return change;
    }

    // Nodes only move within the items' parent or into the nearest list holding it, so normalizing
    // that list, else the parent, covers every move and never targets a removed node
    const nearestList = document
        .getAncestors(parent.key)
        ?.findLast((ancestor) => Block.isBlock(ancestor) && isList(ancestor));
    return change.normalizeNodeByKey((nearestList ?? parent).key);
}

function decreaseItemsDepthWithoutNormalization(change: Change, itemsToMove: List<Block>): Change {
    const { value } = change;
    const { document, selection } = value;

    if (itemsToMove.size === 0) {
        return change;
    }

    // Support if a complete list is selected, from the last list so that the outdented items keep
    // their order
    for (const item of itemsToMove.reverse()) {
        if (LIST_BLOCKS.includes(item.type)) {
            change = decreaseItemsDepthWithoutNormalization(change, item.blockNodes);
        }
    }

    const firstItem = itemsToMove.first() as Block;
    const lastItem = itemsToMove.last() as Block;

    const depth = getItemDepth(value, firstItem);
    // Root items cannot be outdented, outdent the sub-items the selection extends into, from the
    // last sublist so that the outdented items keep their order after the item
    if (depth === 1) {
        itemsToMove.forEach((item) => {
            getSelectedSubItems(selection, item)
                .reverse()
                .forEach((subItems) => {
                    change = decreaseItemsDepthWithoutNormalization(change, subItems);
                });
        });
    }

    const parentList = getListForItem(value, firstItem);
    if (!parentList) {
        return change;
    }
    const parentItem = document.getParent(parentList.key) as Block | void;
    if (!parentItem) {
        return change;
    }
    const parentItemParentList = getListForItem(value, parentItem);
    if (!parentItemParentList) {
        return change;
    }

    // The items following the last selected item will be moved to a sublist of item
    const followingItems = parentList.nodes.skipUntil((i) => i === lastItem).rest() as List<Block>;
    // True if the selected items and the followingItems make the whole
    // parentList, and hence the parentList will be emptied
    const willEmptyCurrentList = parentList.nodes.size === followingItems.size + itemsToMove.size;

    const parentItemIndex = parentItemParentList.getChildIndex(parentItem.key) + 1;
    itemsToMove.forEach((item, index) => {
        change = decreaseSingleItemDepth(
            change,
            item,
            parentList,
            parentItem,
            parentItemIndex + index,
            item === lastItem ? followingItems : undefined
        );
    });

    // Remove the currentList completely if needed
    if (willEmptyCurrentList) {
        change.removeNodeByKey(parentList.key);
    }

    return change;
}

function decreaseSingleItemDepth(
    change: Change,
    item: Block,
    parentList: Block,
    parentItem: Block,
    moveToIndex: number,
    followingItems?: List<Block>
): Change {
    const { value } = change;

    const parentItemParentList = getListForItem(value, parentItem);
    if (!parentItemParentList) {
        return change;
    }

    if (followingItems && !followingItems.isEmpty()) {
        // Add them as sublist of item
        const sublist = createSublist(parentList);

        // Add the sublist
        change.insertNodeByKey(item.key, item.nodes.size, sublist);

        change.moveNodeByKey(item.key, parentItemParentList.key, moveToIndex);

        // Move the followingItems to the sublist
        followingItems.forEach((item, index) =>
            change.moveNodeByKey(item.key, sublist.key, sublist.nodes.size + index)
        );
    } else {
        change.moveNodeByKey(item.key, parentItemParentList.key, moveToIndex);
    }

    return change;
}

function increaseItemsDepthWithoutNormalization(
    change: Change,
    selectedItems: List<Block>
): Change {
    const { value } = change;
    const { document, selection } = value;

    if (selectedItems.size === 0) {
        return change;
    }

    const firstSelectedItem = selectedItems.first();
    if (!firstSelectedItem) {
        return change;
    }

    // Support if a complete list is selected
    for (const item of selectedItems) {
        if (LIST_BLOCKS.includes(item.type)) {
            change = increaseItemsDepthWithoutNormalization(change, item.blockNodes);
        }
    }

    const parentListBlock = getListForItem(value, firstSelectedItem);
    if (!parentListBlock) {
        return change;
    }

    let previousItem = document.getPreviousSibling(firstSelectedItem.key);
    // No items above the first in selection, use it to nest other items under
    if (!previousItem || previousItem.type !== BLOCK_LIST_ITEM) {
        previousItem = firstSelectedItem;
    }

    for (const item of selectedItems) {
        // The first item of a list cannot move, indent the sub-items the selection extends into
        if (parentListBlock.getChildIndex(item.key) === 0) {
            getSelectedSubItems(selection, item).forEach((subItems) => {
                change = increaseItemsDepthWithoutNormalization(change, subItems);
            });
            continue;
        }

        // Other items move along with their sub-items
        change = moveAsSubItem(change, item, previousItem.key);
    }

    return change;
}

/**
 * Move the given item to the sublist at the end of destination item,
 * creating a sublist if needed.
 */
function moveAsSubItem(
    change: Change,
    // The list item to add
    item: Block,
    // The key of the destination node
    destKey: string
): Change {
    const destination = change.value.document.getDescendant<Block>(destKey);
    if (!destination) {
        throw new Error('Destination list item does not exist');
    }
    const lastIndex = destination.nodes.size;
    const lastChild = destination.nodes.last();

    // The potential existing last child list
    const existingList = Block.isBlock(lastChild) && isList(lastChild) ? lastChild : null;
    if (existingList) {
        return change.moveNodeByKey(
            item.key,
            existingList.key,
            existingList.nodes.size // as last item
        );
    }

    const currentList = getListForItem(change.value, destination);
    if (!currentList) {
        throw new Error('Destination is not in a list');
    }

    const newSublist = createSublist(currentList);

    change.insertNodeByKey(destKey, lastIndex, newSublist);

    return change.moveNodeByKey(item.key, newSublist.key, 0);
}

/**
 * Create an empty sublist of the same kind as `list`, numbered from the start.
 */
function createSublist(list: Block): Block {
    return Block.create({
        object: 'block',
        type: list.type,
        data: list.type === BLOCK_LIST_OL ? list.data.delete('start') : list.data,
    });
}

/**
 * Return the items the selection extends into, for each sublist of the selected `item`, in order.
 */
function getSelectedSubItems(selection: SelectionRange, item: Block): List<Block>[] {
    const textSelection = TextRange.isTextRange(selection) ? selection : null;
    if (textSelection && (!textSelection.startKey || !textSelection.endKey)) {
        return [];
    }

    // A block selection covers the whole item. As the item is selected, a text selection edge
    // outside of it lies before (start) or after (end) it.
    const startPath = textSelection?.startKey ? item.getPath(textSelection.startKey) : null;
    const endPath = textSelection?.endKey ? item.getPath(textSelection.endKey) : null;
    const startChildIndex = startPath?.[0] ?? -1;
    const endChildIndex = endPath?.[0] ?? item.nodes.size;

    return item.nodes.toArray().flatMap((child, index) => {
        if (
            index < startChildIndex ||
            index > endChildIndex ||
            !Block.isBlock(child) ||
            !isList(child)
        ) {
            return [];
        }

        // An edge inside the sublist lies in the sub-item at the next index of its path
        const subItems = child.blockNodes;
        const firstIndex = index === startChildIndex ? (startPath?.[1] ?? 0) : 0;
        const lastIndex =
            index === endChildIndex ? (endPath?.[1] ?? subItems.size - 1) : subItems.size - 1;
        const selectedSubItems = subItems.slice(firstIndex, lastIndex + 1);
        return selectedSubItems.isEmpty() ? [] : [selectedSubItems];
    });
}

/*
 * Returns the highest list of blocks that cover the current selection
 */
function getHighestSelectedBlocks({ document, selection }: Value): List<Block> {
    if (!TextRange.isTextRange(selection)) {
        return List.create();
    }
    const { startKey, endKey } = selection;
    if (!startKey || !endKey) {
        return List.create();
    }
    const startBlock = document.getClosestBlock(startKey);
    const endBlock = document.getClosestBlock(endKey);
    if (!startBlock || !endBlock) {
        return List.create();
    }

    if (startBlock === endBlock) {
        return List.create([startBlock]);
    }

    const ancestor = document.getCommonAncestor(startBlock.key, endBlock.key);
    if (!ancestor || ancestor.object === 'text') {
        return List.create();
    }
    const start = ancestor.getPath(startBlock.key)?.[0];
    const end = ancestor.getPath(endBlock.key)?.[0];
    if (start === undefined || end === undefined) {
        return List.create();
    }

    return List.create(
        ancestor.nodes
            .slice(start, end + 1)
            .toArray()
            .filter(Block.isBlock)
    );
}
